import { Chip, FormControl, FormHelperText, TextField, Box, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import type { CSSProperties } from 'react';
import { ChangeEvent, useState, KeyboardEvent } from 'react';

export interface IamPolicyMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  fullWidth?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  style?: CSSProperties;
}

/**
 * IAM Policy Multi-Select component that allows free text entry.
 * Uses chips to display selected values and a text input to add new ones.
 * No dropdown - just chips and a simple input field.
 */
export const IamPolicyMultiSelect = ({
  value,
  onChange,
  label = 'IAM Policies',
  helperText,
  error = false,
  fullWidth = true,
  variant = 'outlined',
  size = 'small',
  style,
}: IamPolicyMultiSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const normalizedValue: string[] = Array.isArray(value) ? value : [];

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !normalizedValue.includes(trimmed)) {
      onChange([...normalizedValue, trimmed]);
      setInputValue('');
    }
  };

  const handleDelete = (policyToDelete: string) => {
    onChange(normalizedValue.filter(policy => policy !== policyToDelete));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} style={style}>
      <Box>
        {normalizedValue.length > 0 && (
          <Box display="flex" flexWrap="wrap" marginBottom={1}>
            {normalizedValue.map(policy => (
              <Chip
                key={policy}
                label={policy}
                onDelete={() => handleDelete(policy)}
                size="small"
                variant="outlined"
                style={{ marginRight: 8, marginBottom: 8 }}
              />
            ))}
          </Box>
        )}
        <Box display="flex" alignItems="flex-start">
          <TextField
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            variant={variant}
            label={label}
            error={error}
            size={size}
            placeholder={helperText || 'Enter policy name and press Enter'}
            fullWidth
          />
          <IconButton
            onClick={handleAdd}
            size="small"
            color="primary"
            style={{ marginTop: size === 'small' ? 4 : 8 }}
          >
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
